import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useCreatePath, useNotify } from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import { currentSearch } from './AvizationQueue';
import { MessageDraft } from './MessageDraft';
import {
  FEATURE_CLIENT_UPDATES,
  MIN_REASON,
  SKIP_REASONS,
  fetchClientUpdates,
  filterFromSearch,
  markClientUpdateSent,
  saveClientUpdate,
  skipClientUpdate,
  toErrorMessage,
  type ClientUpdate,
} from './clientCommunicationApi';

const UPDATE_STATES = [
  { id: 'proposed', name: 'do wysłania' },
  { id: 'sent_manually', name: 'wysłane ręcznie' },
  { id: 'skipped', name: 'pominięte' },
  { id: 'superseded', name: 'zastąpione nowszą' },
  { id: 'all', name: 'wszystkie' },
];

/** Jedna aktualizacja: zdarzenie, reguła klienta, szkic i decyzja CS. */
export const ClientUpdateCard = ({
  u,
  canWrite,
  onChanged,
}: {
  u: ClientUpdate;
  canWrite: boolean;
  onChanged: (next: ClientUpdate) => void;
}) => {
  const notify = useNotify();
  const createPath = useCreatePath();
  const [open, setOpen] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const proposed = u.state === 'proposed';

  const run = async (action: () => Promise<ClientUpdate>, message: string) => {
    setBusy(true);
    try {
      onChanged(await action());
      notify(message, { type: 'success' });
      setSkipping(false);
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
      data-testid={`client-update-${u.id}`}
      data-state={u.state}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
        <Link to={createPath({ resource: 'user-offers', type: 'show', id: u.userOfferId })}>{u.orderNumber}</Link>
        <Typography variant="body2">{u.clientName ?? '—'}</Typography>
        <Chip size="small" variant="outlined" label={u.categoryLabel} />
        <Chip
          size="small"
          color={proposed ? (u.overdue ? 'error' : 'warning') : u.state === 'sent_manually' ? 'success' : 'default'}
          label={proposed && u.overdue ? 'po terminie — przypomnienie' : u.stateLabel}
        />
        <Chip
          size="small"
          color={u.ruleState === 'required' ? 'info' : 'default'}
          variant="outlined"
          label={u.ruleStateLabel}
          data-testid={`client-update-rule-${u.id}`}
        />
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.5 }} data-testid={`client-update-event-${u.id}`}>
        {u.eventText}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        zdarzenie {dateTimeNoSeconds(u.eventAt)}
        {u.sentByName ? ` · wysłane ręcznie: ${u.sentByName}, ${dateTimeNoSeconds(u.sentAt)}` : ''}
        {u.skipReason ? ` · pominięte (${u.skippedByName ?? '—'}): ${u.skipReason}` : ''}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Button size="small" variant={proposed ? 'contained' : 'text'} onClick={() => setOpen((v) => !v)} data-testid={`client-update-preview-${u.id}`}>
          {open ? 'Zwiń podgląd' : proposed && canWrite ? 'Podgląd i wyślij' : 'Podgląd'}
        </Button>
        {proposed && canWrite ? (
          <Button size="small" disabled={busy} onClick={() => setSkipping((v) => !v)} data-testid={`client-update-skip-${u.id}`}>
            Pomiń z powodem
          </Button>
        ) : null}
      </Box>
      {skipping ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" onClick={() => setReason(SKIP_REASONS[2])}>
              {SKIP_REASONS[2]}
            </Button>
          </Box>
          <TextField
            size="small"
            label="Powód pominięcia"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={`co najmniej ${MIN_REASON} znaków`}
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': `client-update-reason-${u.id}` } }}
          />
          <Box>
            <Button
              size="small"
              variant="contained"
              disabled={busy || reason.trim().length < MIN_REASON}
              onClick={() => void run(() => skipClientUpdate(u.id, reason), 'Aktualizacja pominięta')}
              data-testid={`client-update-skip-confirm-${u.id}`}
            >
              Pomiń
            </Button>
          </Box>
        </Stack>
      ) : null}
      {open ? (
        <Box sx={{ mt: 1 }}>
          <MessageDraft
            subject={u.subject}
            body={u.body}
            recipient={u.recipient}
            editable={proposed && canWrite}
            busy={busy}
            testIdPrefix={`client-update-${u.id}`}
            onSave={(v) => run(() => saveClientUpdate(u.id, { subject: v.subject, body: v.body, recipient: v.recipient }), 'Zapisano poprawki')}
            onSent={() => run(() => markClientUpdateSent(u.id), 'Aktualizacja oznaczona jako wysłana ręcznie')}
          />
        </Box>
      ) : null}
    </Box>
  );
};

/**
 * Aktualizacje dla klienta (makieta p. Mariusza: ekran „Aktualizacje dla
 * klienta”; prace 26.10.2026, pakiet P1; przełącznik `clientUpdates`,
 * S-26.10-15; BR-CS-09): propozycje ze zdarzeń w dzienniku zlecenia — zmiana
 * pojazdu, przewoźnik, termin w TMS, zaakceptowana zmiana, anulowanie.
 * Reguła klienta (karta kontrahenta) filtruje; bez reguły — z oznaczeniem.
 * Nowa propozycja tego samego rodzaju zastępuje niewysłaną.
 */
export const ClientUpdatesScreen = () => {
  const on = useFeatureState(FEATURE_CLIENT_UPDATES);
  const initial = useMemo(() => filterFromSearch(currentSearch()), []);
  const [state, setState] = useState(typeof initial.state === 'string' ? initial.state : 'proposed');
  const [items, setItems] = useState<ClientUpdate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (on !== true) return;
    fetchClientUpdates(state)
      .then((r) => {
        setItems(r.items);
        setError(null);
      })
      .catch((e) => setError(toErrorMessage(e)));
  }, [on, state]);
  useEffect(load, [load]);

  if (on === undefined) return <FeaturesLoading />;
  if (!on) return null;

  return (
    <Box sx={{ p: 1 }} data-testid="client-updates">
      <Title title="Aktualizacje dla klienta" />
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Propozycje informacji dla klienta po zdarzeniach w zleceniu. Sprawdź treść, wyślij ją ze swojej poczty
            i oznacz „Wysłane ręcznie” — albo pomiń z powodem. O czym klient chce wiedzieć, mówi reguła na karcie
            kontrahenta.
          </Typography>
          <TextField
            select
            size="small"
            label="Stan"
            value={state}
            onChange={(e) => setState(e.target.value)}
            sx={{ mb: 1, minWidth: 200 }}
            slotProps={{ htmlInput: { 'data-testid': 'client-updates-state' } }}
          >
            {UPDATE_STATES.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {items && items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="client-updates-empty">
              Brak aktualizacji w tym widoku.
            </Typography>
          ) : null}
          <Stack spacing={1}>
            {(items ?? []).map((u) => (
              <ClientUpdateCard
                key={u.id}
                u={u}
                canWrite
                onChanged={(next) => setItems((list) => (list ?? []).map((x) => (x.id === next.id ? next : x)))}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};
